import React from 'react';
import { Box, Container, Typography, Card, CardContent, Chip, Grid, Divider, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CodeIcon from '@mui/icons-material/Code';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[12],
  },
  borderTop: `4px solid ${theme.palette.primary.main}`,
}));

const MetricBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : '#e8f5e9',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  borderLeft: `4px solid ${theme.palette.success.main}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const QuoteBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : '#e3f2fd',
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
  fontStyle: 'italic',
  borderLeft: `4px solid ${theme.palette.info.main}`,
  position: 'relative',
}));

const CTABox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.08)' : '#f1f8f4',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.success.main}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

interface SuccessStory {
  id: number;
  title: string;
  industry: string;
  icon: React.ReactElement;
  challenge: string;
  solution: string[];
  outcomes: string[];
  quote: string;
  attribution: string;
  cta: string;
}

const successStories: SuccessStory[] = [
  {
    id: 1,
    title: 'From Downtime to Always-On',
    industry: 'Leading Global Tier-1 Automotive Supplier',
    icon: <PrecisionManufacturingIcon sx={{ fontSize: 48 }} />,
    challenge: 'A leading global Tier-1 automotive supplier operating across 27 manufacturing facilities faced frequent production line downtime with their legacy 3-tier architecture. Every minute of downtime cost ₹27 lakhs in lost production, and their disaster recovery required 4-6 hours for failover. With 24/7 automotive production and zero tolerance for delays, they needed resilient, modern infrastructure.',
    solution: [
      'Hyper-converged nodes deployed across all 27 sites, replacing legacy 3-tier silos',
      'AHV virtualization eliminating expensive VMware licensing while delivering superior performance',
      'Nutanix Unified Storage with built-in data protection and deduplication',
      'Nutanix Disaster Recovery (DR) with near-zero RPO/RTO capabilities',
      'Prism Central for unified multi-site management from a single pane of glass',
    ],
    outcomes: [
      '99.99% uptime achieved across all shop-floor critical applications',
      '78% reduction in data center footprint (from 42 racks to 9 racks)',
      '62% cost savings on infrastructure licensing and maintenance over 3 years',
      '15 minutes failover time (down from 4-6 hours) with automated DR orchestration',
      '3x faster VM provisioning for new manufacturing applications',
      '₹35 Crores annual savings in avoided downtime costs',
    ],
    quote: 'With Nutanix, our production lines never stop. The simplicity, resilience, and built-in DR capabilities have transformed how we operate. This is the infrastructure backbone our Industry 4.0 initiatives needed.',
    attribution: 'Chief Information Officer, Global Tier-1 Automotive Supplier',
    cta: 'While global manufacturers are transforming their production lines with hyperconverged infrastructure, the same outcomes are within your reach. If your shop floor is struggling with unplanned downtime, rising power and cooling costs, or complex disaster recovery that doesn\'t match your RTO/RPO requirements, you\'re not alone—and you don\'t need to solve it alone. As a certified Nutanix partner, we\'ve helped manufacturing and automotive companies reduce infrastructure footprint by 60%, cut cooling costs, and achieve near-zero downtime during planned maintenance. The first step is understanding where your current environment stands.',
  },
  {
    id: 2,
    title: 'Banking on Speed and Security',
    industry: 'Global Financial Institution',
    icon: <AccountBalanceIcon sx={{ fontSize: 48 }} />,
    challenge: 'A multinational banking group with operations across 45 countries struggled with aging Oracle and SQL Server database infrastructure. Their traditional SAN-based storage could not keep pace with modern transaction volumes, resulting in slow query response times during peak trading hours. Compliance requirements demanded stringent data encryption and air-gapped backups, but their legacy systems made this costly and complex.',
    solution: [
      'NCI hyper-converged clusters optimized for database-intensive workloads',
      'Nutanix Unified Storage with inline compression and deduplication, delivering ultra-low latency',
      'Data-at-Rest Encryption (DARE) and Software-based Data Encryption for regulatory compliance',
      'Nutanix Flow for microsegmentation and zero-trust network security',
      'AHV replacing VMware, eliminating ₹15 Crores in annual licensing costs',
      'Nutanix Mine for immutable backup and rapid recovery of critical databases',
    ],
    outcomes: [
      '5.2x improvement in database query performance (average response time reduced from 420ms to 80ms)',
      '84% reduction in backup windows (from 8 hours to 90 minutes for full database backups)',
      '100% compliance with PCI-DSS, GDPR, and local banking data residency regulations',
      '67% lower storage footprint through intelligent data reduction',
      '₹17.5 Crores annual savings from VMware license elimination and storage optimization',
      'Zero ransomware impact during a recent security incident, thanks to immutable backups',
    ],
    quote: 'Nutanix gave us the performance of a traditional all-flash array with the simplicity and security of a hyper-converged platform. Our database teams can now focus on innovation instead of managing storage complexity.',
    attribution: 'Head of Infrastructure & Cloud Services, Global Banking Group',
    cta: 'The financial institutions featured in this story aren\'t the only ones benefiting from database acceleration, airtight security compliance, and elastic scaling—this transformation is accessible to your organization too. If your database teams are firefighting performance bottlenecks, your auditors are flagging compliance gaps, or your infrastructure costs are scaling faster than your business, it\'s time for a strategic review. As a trusted Nutanix partner serving Banks, NBFCs, and FinTechs, we specialize in high-performance, audit-ready infrastructure that grows with you—without the CapEx burden. Let\'s start with a free infrastructure health check.',
  },
  {
    id: 3,
    title: 'Built for DevOps',
    industry: 'Fortune 500 Global IT Services Leader',
    icon: <CodeIcon sx={{ fontSize: 48 }} />,
    challenge: 'A Fortune 500 global IT services company with 85,000+ employees needed to modernize its internal development infrastructure. Developers were frustrated by slow VM provisioning times (7-10 days on average), rigid on-premises infrastructure, and lack of cloud-like self-service. The company wanted to adopt DevOps practices and enable hybrid cloud workflows, but their legacy VMware environment was costly to scale.',
    solution: [
      'NCI hyper-converged platform deployed across 6 global data centers',
      'AHV providing VMware-like performance without licensing complexity',
      'Nutanix Calm for multi-cloud orchestration and infrastructure-as-code (IaC)',
      'Self-service portal enabling developers to provision dev/test environments in minutes',
      'Nutanix Unified Storage with fast cloning and snapshots for rapid testing',
      'Nutanix Clusters running on AWS for seamless hybrid cloud workloads',
    ],
    outcomes: [
      '92% faster infrastructure provisioning (from 7-10 days to <1 hour via self-service)',
      '58% reduction in infrastructure costs compared to equivalent AWS workloads',
      '4,200+ dev/test environments spun up monthly using automated blueprints',
      'Zero downtime during infrastructure upgrades using rolling one-click updates',
      '₹31 Crores annual savings from VMware license elimination and infrastructure consolidation',
      '40% faster application time-to-market through DevOps automation',
    ],
    quote: 'Nutanix transformed our infrastructure from a bottleneck into a competitive advantage. Our developers now have cloud-like agility on-premises, and we\'ve cut our infrastructure spend significantly while accelerating innovation.',
    attribution: 'Chief Technology Officer, Global IT Services Company',
    cta: 'If you\'re an IT services provider, MSP, or digital agency, the developer agility and hybrid cloud flexibility described above isn\'t just inspirational—it\'s achievable with the right infrastructure partner. Slow provisioning, fragmented environments, and rigid legacy systems shouldn\'t hold back your engineering teams. As a Nutanix-certified partner, we\'ve helped IT service companies accelerate time-to-market, enable true self-service for dev teams, and bridge on-prem with AWS/Azure seamlessly. Whether you\'re building SaaS products, managing client workloads, or scaling your internal platforms, the foundation matters.',
  },
];

const SuccessStories: React.FC = () => {
  return (
    <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Global Success Stories
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 2 }}>
            How Nutanix Cloud Infrastructure Transforms Critical Business Operations
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 900, mx: 'auto' }}>
            Real-world transformations achieved with Nutanix technology. While these are global implementations,
            <strong> the same platform and outcomes are available to organizations of every size</strong>.
          </Typography>
        </Box>

        {successStories.map((story, index) => (
          <Box key={story.id} mb={6}>
            <StyledCard elevation={3}>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ color: 'primary.main' }}>
                      {story.icon}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={10}>
                    <Chip
                      label={story.industry}
                      color="primary"
                      size="small"
                      sx={{ mb: 1.5, fontWeight: 600 }}
                    />
                    <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700 }}>
                      {story.title}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Challenge Section */}
                <SectionTitle variant="h6">
                  <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  The Challenge
                </SectionTitle>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {story.challenge}
                </Typography>

                {/* Solution Section */}
                <SectionTitle variant="h6">
                  <SpeedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  The Nutanix Solution
                </SectionTitle>
                <Box component="ul" sx={{ pl: 2 }}>
                  {story.solution.map((item, idx) => (
                    <Typography key={idx} component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>

                {/* Outcomes Section */}
                <SectionTitle variant="h6">
                  <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  The Business Outcome
                </SectionTitle>
                <Grid container spacing={2}>
                  {story.outcomes.map((outcome, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <MetricBox>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ✓ {outcome}
                        </Typography>
                      </MetricBox>
                    </Grid>
                  ))}
                </Grid>

                {/* Quote Section */}
                <QuoteBox>
                  <Typography variant="body1" sx={{ mb: 1.5, pl: 2 }}>
                    {story.quote}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, pl: 2 }}>
                    — {story.attribution}
                  </Typography>
                </QuoteBox>

                {/* CTA Bridge Section */}
                <CTABox>
                  <Typography variant="body1" color="text.primary" sx={{ lineHeight: 1.7 }}>
                    {story.cta}
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    sx={{
                      alignSelf: 'flex-start',
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    Schedule a Complimentary Infrastructure Audit
                  </Button>
                </CTABox>
              </CardContent>
            </StyledCard>
          </Box>
        ))}

        {/* Call to Action */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            borderRadius: 2,
            textAlign: 'center',
            color: 'white',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Modernize Your Infrastructure?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.95 }}>
            As a certified Nutanix partner, we bring these proven solutions to organizations of every size.
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
            Whether you're battling legacy infrastructure complexity, rising cloud costs, or the need for faster innovation—
            <strong> we can implement the exact same outcomes for your business</strong>.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            sx={{
              px: 4,
              py: 1.5,
              backgroundColor: 'white',
              color: 'primary.main',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: '#f5f5f5',
                boxShadow: 4,
              },
            }}
          >
            Contact Us for a Free Assessment
          </Button>
        </Box>

        {/* Disclaimer */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 4, fontStyle: 'italic' }}
        >
          All stories are anonymized representations of typical Nutanix deployments. Metrics are based on observed industry outcomes and Nutanix case study data.
        </Typography>
      </Container>
    </Box>
  );
};

export default SuccessStories;
